import type { RemovableRef, UseStorageOptions } from '@vueuse/core'
import { useLocalStorage } from '@vueuse/core'
import { ref } from 'vue'

/**
 * Returns true when `window.localStorage` can be safely accessed.
 *
 * Inside embed iframes whose host blocks storage, merely reading the
 * `window.localStorage` property throws a SecurityError (e.g. "Failed to read
 * the 'localStorage' property from 'Window': Access is denied for this
 * document."), so the probe is wrapped in try/catch.
 */
export function isLocalStorageAvailable(): boolean {
  try {
    // Touching the property is what throws in blocked-storage iframes.
    void window.localStorage
    return true
  }
  catch {
    return false
  }
}

/**
 * Drop-in replacement for VueUse's `useLocalStorage` that degrades gracefully
 * when localStorage is unavailable (e.g. embed iframes on third-party hosts).
 *
 * When storage is reachable it behaves exactly like `useLocalStorage`. When it
 * is blocked, it returns a plain, non-persistent ref seeded with the default
 * value so consuming components still mount instead of throwing.
 */
export function useSafeLocalStorage<T>(
  key: string,
  defaultValue: T,
  options?: UseStorageOptions<T>,
): RemovableRef<T> {
  if (isLocalStorageAvailable()) {
    return useLocalStorage(key, defaultValue, options)
  }
  return ref(defaultValue) as RemovableRef<T>
}
